import React, { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import "./LandingPage.css";
import { getAgencyByDirectorID } from "../store/actions/index";
import {
  setProductionStatisticsForAgency,
  setProductionStatisticsForAgencyByDirectorID,
} from "../store/actions/production/productionStatisticsForAgency";
import {
  setPlanChange,
  setPlanChangeByDirectorID,
} from "../store/actions/plan-change/PlanChange";
import { setETLDates } from "../store/actions/production/productionActions";
import LoadingOverlay from "@ronchalant/react-loading-overlay";
import history from "../utils/history";
import {
  getProductionStatisticsForAgencyFullAction,
  getProductionStatisticsForAgencyFullActionByDirectorID,
} from "../store/actions/production/productionStatisticsForAgencyFull";
import {
  fetchReadyToSellList,
  fetchReadyToSellListByDirectorID,
} from "../store/actions/agents/readyToSell";
import {
  fetchAgentContractList,
  fetchAgentContractListByDirectorID,
} from "../store/actions/agents/agentsContractInfo";
import {
  fetchAgentReadyToSellInfo,
  fetchAgentReadyToSellInfoByDirectorID,
} from "../store/actions/agents/agentsReadyToSellInfo";
import {
  fetchAgentList,
  fetchAgentListByDirectorID,
} from "../store/actions/agents/agents";
import {
  fetchAgentReadyToSellByCarrier,
  fetchAgentReadyToSellByCarrierByDirectorID,
} from "../store/actions/agents/readyToSellByCarrier.js";
import {
  fetchPrincipals,
  fetchPrincipalsByDirectorID,
} from "../store/actions/agents/principals";
import {
  fetchNotReadyToSell,
  fetchNotReadyToSellByDirectorID,
} from "../store/actions/agents/notReadyToSell";
import {
  fetchProductionCarrierAndQtrData,
  fetchProductionCarrierAndQtrDataByDirectorID,
  fetchProductionSalesYear,
} from "../store/actions/production/productionLineGraph";
import { getStatesMethod } from "../store/actions/dashboard/contractForm";
import {
  setContractStatuses,
  setContractStatusesByDirectorID,
} from "../store/actions/contracts";

const useStyles = makeStyles()((theme) => {
  return {
    root: {
      textAlign: "center",
      margin: "0px",
      display: "grid",
      webkitBackgroundSize: "cover",
      mozBackgroundSize: "cover",
      backgroundSize: "16px 16px",
      minHeight: "100vh",
      background: "#424242",
      backgroundColor: "rgb(68, 66, 66)",
      paddingBottom: 20,
    },
    overlay: {
      height: "100%",
    },
  };
});

const LandingPage = () => {
  const [loaderText, setLoaderText] = useState("Configuring Agency...");
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const [isDataLoaded, setIsDataLoaded] = useState(
    useSelector(
      ({ productionStatisticsForAgency }) =>
        productionStatisticsForAgency.isLoading
    )
  );
  const navigate = useCallback((path) => {
    history.push(path);
  }, []);

  const processAgencyID = useCallback(
    (agencyId) => {
      setTimeout(async () => {
        setLoaderText("Gathering Data...");
        await Promise.all([
          dispatch(
            getProductionStatisticsForAgencyFullAction(parseInt(agencyId))
          ),
          dispatch(setETLDates()),
          dispatch(fetchAgentReadyToSellByCarrier(parseInt(agencyId))),
          dispatch(fetchProductionSalesYear()),
          dispatch(setPlanChange(agencyId)),
          dispatch(fetchAgentReadyToSellInfo(parseInt(agencyId))),
          dispatch(fetchAgentList(parseInt(agencyId))),
          dispatch(fetchAgentContractList(parseInt(agencyId))),
          dispatch(getStatesMethod()),
        ])
          .then(async () => {
            await Promise.all([
              dispatch(setProductionStatisticsForAgency(agencyId)),
              dispatch(fetchReadyToSellList(agencyId)),
              dispatch(fetchPrincipals(parseInt(agencyId))),
            ]);
          })
          .then(async () => {
            await Promise.all([
              dispatch(fetchProductionCarrierAndQtrData(agencyId)),
              dispatch(setContractStatuses(agencyId)),
              dispatch(fetchNotReadyToSell(parseInt(agencyId))),
            ]);
          })
          .then(() => {
            setIsDataLoaded(true);
          });
      }, 0);
    },
    [dispatch, setLoaderText, setIsDataLoaded]
  );

  const processAgencyIDs = useCallback(
    (directorId) => {
      setTimeout(async () => {
        setLoaderText("Gathering Data...");
        await Promise.all([
          dispatch(
            getProductionStatisticsForAgencyFullActionByDirectorID(directorId)
          ),
          dispatch(getAgencyByDirectorID(directorId)),
          dispatch(setETLDates()),
          dispatch(fetchAgentReadyToSellByCarrierByDirectorID(directorId)),
          dispatch(fetchProductionSalesYear()),
          dispatch(setPlanChangeByDirectorID(directorId)),
          dispatch(fetchAgentReadyToSellInfoByDirectorID(directorId)),
          dispatch(fetchAgentListByDirectorID(directorId)),
          dispatch(fetchAgentContractListByDirectorID(directorId)),
          dispatch(getStatesMethod(directorId)),
        ])
          .then(async () => {
            await Promise.all([
              dispatch(
                setProductionStatisticsForAgencyByDirectorID(directorId)
              ),
              dispatch(fetchReadyToSellListByDirectorID(directorId)),
              dispatch(fetchPrincipalsByDirectorID(directorId)),
            ]);
          })
          .then(async () => {
            await Promise.all([
              dispatch(
                fetchProductionCarrierAndQtrDataByDirectorID(directorId)
              ),
              dispatch(setContractStatusesByDirectorID(directorId)),
              dispatch(fetchNotReadyToSellByDirectorID(directorId)),
            ]);
          })
          .then(() => {
            setIsDataLoaded(true);
          });
      }, 0);
    },
    [dispatch, setLoaderText, setIsDataLoaded]
  );

  useEffect(() => {
    setTimeout(() => setLoaderText("Collecting Agents..."), 2500);
    setTimeout(() => setLoaderText("Producing Tables..."), 5000);
    setTimeout(() => setLoaderText("Verifying Data..."), 7500);
    setTimeout(() => setLoaderText("Loading Data..."), 10000);
    setTimeout(() => setLoaderText("Still Loading..."), 15000);
    setTimeout(() => setLoaderText("Still Loading......"), 20000);
    setTimeout(() => setLoaderText("Still Loading........."), 25000);
    setTimeout(() => setLoaderText("Still Loading............"), 30000);
    setTimeout(() => setLoaderText("Still Loading........."), 35000);
    setTimeout(() => setLoaderText("Still Loading..."), 40000);
    setTimeout(() => setLoaderText("Almost There..."), 45000);
  }, []);

  const LoadInitData = useCallback(() => {
    const agencyId = sessionStorage.getItem("agencyId");
    const directorId = sessionStorage.getItem("directorId");

    if (agencyId !== "" && agencyId !== "undefined") {
      processAgencyID(agencyId);
    } else if (directorId !== "" && directorId !== "undefined") {
      processAgencyIDs(parseInt(directorId));
    }
  }, [processAgencyID, processAgencyIDs]);

  useEffect(() => {
    LoadInitData();
    const parent = sessionStorage.getItem("parent");
    if (parent === undefined || parent === null) {
      sessionStorage.setItem("parent", "true");
      sessionStorage.setItem("parentAgency", "");
    }
  }, [LoadInitData]);

  isDataLoaded && navigate("/");

  return (
    <LoadingOverlay
      active={!isDataLoaded}
      className={classes.overlay}
      styles={{
        spinner: (base) => ({
          ...base,
          width: "100px",
        }),
      }}
      spinner
      text={<Typography style={{ fontSize: 20 }}>{`${loaderText}`}</Typography>}
    >
      <Grid
        className={classes.root}
        container
        spacing={0}
        direction="column"
        alignItems="center"
      />
    </LoadingOverlay>
  );
};

export default LandingPage;
